import React from 'react';
import { Badge } from '../ui/badge';
import { ChevronRight, Award, TrendingUp, CheckCircle2, PlayCircle, Check } from 'lucide-react';

/**
 * TestItem component for displaying individual tests
 * Updated to show completion status like LevelRoadmap
 */
const TestItem = ({ test, onTestClick, locked = false, lockReason = '', onLockedClick }) => {
  const getTestIcon = () => {
    switch (test.exam_type) {
      case 'Final Exam':
        return Award; // Module Test
      case 'Master Test':
        return TrendingUp; // Lesson Test  
      case 'Basic Exam':
        return CheckCircle2; // Topic Test
      default:
        return PlayCircle;
    }
  };

  const Icon = getTestIcon();
  const isCompleted = test.isCompleted || false;
  const durationLabel = test.duration ? `${test.duration} min` : "30 min";

  const handleInteraction = () => {
    if (locked) {
      onLockedClick?.(lockReason);
      return;
    }

    onTestClick(test);
  };

  return (
    <div
      className={`group flex items-center gap-2.5 md:gap-3 justify-between w-full max-w-full overflow-hidden min-w-0 rounded-2xl border p-2.5 md:p-4 transition hover:shadow-lg cursor-pointer mb-3 ${isCompleted
          ? 'border-green-200 bg-gradient-to-r from-green-50 to-emerald-50/80'
          : 'border-slate-200 bg-gradient-to-r from-white to-slate-50/80 hover:border-blue-200'
        }`}
      onClick={handleInteraction}
    >
      <div className="flex items-center gap-2.5 md:gap-3 min-w-0 flex-1">
        <div className={`flex h-9 w-9 md:h-10 md:w-10 items-center justify-center rounded-xl md:rounded-2xl shrink-0 ${isCompleted
            ? 'bg-green-600/10 text-green-600'
            : 'bg-blue-600/10 text-blue-600'
          }`}>
          {isCompleted ? (
            <Check className="h-4.5 w-4.5 md:h-5 md:w-5" />
          ) : (
            <Icon className="h-4.5 w-4.5 md:h-5 md:w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[13px] md:text-sm font-bold truncate ${isCompleted ? 'text-green-900' : 'text-slate-900'
            }`}>{test.title}</p>
          <div className="mt-0.5 md:mt-1 flex flex-wrap items-center gap-1.5 md:gap-2 text-[10px] md:text-[12px] text-slate-500 font-medium">
            <span className={`shrink-0 ${isCompleted ? 'text-green-600' : 'text-slate-500'
              }`}>{test.exam_type?.split(' ')[0]}</span>
            <Badge variant="secondary" className={`rounded-full px-1.5 py-0 md:px-2 md:py-0.5 text-[9px] md:text-[11px] font-bold border-none uppercase tracking-tighter ${isCompleted
                ? 'bg-green-100 text-green-700'
                : 'bg-blue-100 text-blue-700'
              }`}>
              {durationLabel}
            </Badge>
          </div>
        </div>
      </div>
      <button
        type="button"
        className={`shrink-0 rounded-full px-2.5 md:px-3 py-1.5 text-[10px] md:text-[11px] font-black uppercase tracking-widest transition shadow-sm ${isCompleted
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        onClick={(e) => {
          e.stopPropagation();
          handleInteraction();
        }}
        disabled={locked}
      >
        {isCompleted ? 'Review' : 'Take'}
      </button>
    </div>
  );
};

export default TestItem;
